<template>
  <div class="login-container">
    <div class="login-wrapper">
      <div class="login-content">
        <!-- 로고 영역 -->
        <div class="logo-area">
          <img src="@/assets/images/logo/login_logo.png" alt="NOSP Inventory" />
        </div>
        
        <!-- 로그인 폼 영역 -->
        <div class="login-form-area">
          <h1 class="login-title">NOSP Inventory Dashboard</h1>
          
          <form @submit.prevent="loginProcess" class="login-form">
            <!-- 아이디 입력 -->
            <div class="form-group">
              <label for="userId">아이디</label>
              <div class="input-with-icon">
                <i class="input-icon user-icon">👤</i>
                <input
                  id="userId"
                  type="text"
                  v-model="loginInput.userId"
                  placeholder="아이디(이메일)를 입력하세요"
                  class="form-control"
                />
              </div>
            </div>
            
            <!-- 비밀번호 입력 -->
            <div class="form-group">
              <label for="password">비밀번호</label>
              <div class="input-with-icon">
                <i class="input-icon lock-icon">🔒</i>
                <input
                  id="password"
                  :type="active ? 'password' : 'text'"
                  v-model="loginInput.password"
                  placeholder="비밀번호를 입력하세요"
                  class="form-control"
                />
                <span 
                  class="toggle-password" 
                  :class="active ? 'show' : 'hide'" 
                  @click.prevent="show"
                ></span>
              </div>
            </div>
            
            <!-- 로그인 버튼 -->
            <button type="submit" class="login-btn">로그인</button>
          </form>
        </div>
        
        <!-- 푸터 -->
        <div class="login-footer">
          <p>CopyrightⓒDMCMedia Since2025. All Rights Reserved</p>
        </div>
      </div>
    </div>
    
    <!-- 배경 장식 -->
    <div class="login-decoration"></div>
  </div>
</template>

<script setup>
import { ref, onMounted, defineComponent } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { doLogin } from '@/api/user';
import { swalAlert } from '@/utils/swal';

defineComponent({
  name: 'LoginView'
});

const router = useRouter();
const route = useRoute();

const loginInput = ref({
  userId: '',
  password: '',
});
const active = ref(true);

const show = () => {
  active.value = !active.value;
};

const loginProcess = async () => {
  if (loginInput.value.userId === '') {
    swalAlert('아이디를 입력해주세요.', 'warning');
  } else if (loginInput.value.password === '') {
    swalAlert('비밀번호를 입력해주세요.', 'warning');
  } else {
    loginInput.value.userId = loginInput.value.userId.trim();

    let loginModel = ref({
      "requestModel": {
        "username": loginInput.value.userId,
        "password": loginInput.value.password
      }
    });

    await doLogin(router, loginModel.value);
  }
};

const siteType = ref('');
onMounted(() => {
  siteType.value = route.query.site;
});
</script>

<style scoped>
/* 전체 로그인 컨테이너 */
.login-container {
  display: flex;
  min-height: 100vh;
  width: 100%;
  background-color: #f8f9fa;
  position: relative;
  overflow: hidden;
}

/* 로그인 콘텐츠 래퍼 */
.login-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  z-index: 1;
}

/* 로그인 콘텐츠 영역 */
.login-content {
  width: 100%;
  max-width: 440px;
  background: white;
  border-radius: 16px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.06);
  padding: 40px;
  margin: 20px;
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 32px;
}

/* 로고 영역 */
.logo-area {
  display: flex;
  justify-content: center;
}

.logo-area img {
  height: 40px;
  object-fit: contain;
}

/* 로그인 폼 영역 */
.login-form-area {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.login-title {
  font-size: 24px;
  font-weight: 700;
  color: #333;
  margin: 0;
  text-align: center;
}

.login-subtitle {
  font-size: 14px;
  color: #666;
  margin: 0;
  text-align: center;
  margin-bottom: 16px;
}

/* 로그인 폼 */
.login-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-group label {
  font-size: 14px;
  font-weight: 600;
  color: #333;
}

.input-with-icon {
  position: relative;
}

.input-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 16px;
  color: #999;
}

.form-control {
  width: 100%;
  height: 48px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 0 40px;
  font-size: 14px;
  transition: all 0.3s ease;
  box-sizing: border-box;
}

.form-control:focus {
  border-color: #1a73e8;
  outline: none;
  box-shadow: 0 0 0 3px rgba(26, 115, 232, 0.1);
}

.form-control::placeholder {
  color: #aaa;
}

.toggle-password {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  width: 20px;
  height: 20px;
  cursor: pointer;
  background-repeat: no-repeat;
  background-size: contain;
  opacity: 0.6;
  transition: opacity 0.3s;
}

.toggle-password:hover {
  opacity: 1;
}

.toggle-password.show {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%23666'%3E%3Cpath d='M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z'/%3E%3C/svg%3E");
}

.toggle-password.hide {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%23666'%3E%3Cpath d='M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z'/%3E%3C/svg%3E");
}

/* 로그인 버튼 */
.login-btn {
  height: 48px;
  background: linear-gradient(135deg, #1a73e8, #3f90f7);
  border: none;
  border-radius: 8px;
  color: white;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 12px;
}

.login-btn:hover {
  background: linear-gradient(135deg, #1967d2, #1a73e8);
  box-shadow: 0 4px 8px rgba(26, 115, 232, 0.2);
}

/* 푸터 */
.login-footer {
  text-align: center;
  color: #9e9e9e;
  font-size: 12px;
  margin-top: 8px;
}

/* 배경 장식 */
.login-decoration {
  position: absolute;
  top: -100px;
  right: -100px;
  width: 500px;
  height: 500px;
  border-radius: 50%;
  background: linear-gradient(135deg, rgba(79, 172, 254, 0.2), rgba(26, 115, 232, 0.2));
  z-index: 0;
}

.login-decoration::before {
  content: '';
  position: absolute;
  bottom: -100px;
  left: -400px;
  width: 300px;
  height: 300px;
  border-radius: 50%;
  background: linear-gradient(135deg, rgba(26, 115, 232, 0.1), rgba(79, 172, 254, 0.1));
}

@media (max-width: 480px) {
  .login-content {
    padding: 24px;
    margin: 16px;
  }
  
  .login-title {
    font-size: 20px;
  }
  
  .form-control {
    height: 44px;
  }
  
  .login-btn {
    height: 44px;
  }
}
</style> 